import { ContainerFluid } from '@/components/container/Container';
import { useHomeQuery } from '@/queries/home.query';
import { formatDateIndo } from '@/utils/formatDate';
import { motion } from 'framer-motion';
import { ArrowUpRight, Calendar, MoveLeft, MoveRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { FadeInStagger } from './animation/FadeIn';
import { BodySmHTML, H5 } from './ui/typography/Typography';

function NewsHome() {
  const sliderRef = useRef(null);
  const autoplaySpeed = 3000;
  const fadeDuration = 2000;

  const { data, isLoading, isError } = useHomeQuery();

  const newsData = data?.data?.recent_news?.slice(0, 4) ?? [];

  const [progress, setProgress] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const resetProgress = () => setProgress(Array(newsData.length).fill(0));

  useEffect(() => {
    if (newsData.length <= 1) return; // jangan autoplay jika cuma 1 data
    const interval = setInterval(() => {
      setProgress((prev) => {
        const updated = [...prev];
        updated[currentSlide] = Math.min(updated[currentSlide] + 100 / (autoplaySpeed / 100), 100);
        return updated;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [currentSlide, autoplaySpeed, newsData.length]);

  const settings = {
    dots: false,
    infinite: newsData.length > 1,
    speed: fadeDuration,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: newsData.length > 1,
    autoplaySpeed,
    cssEase: 'ease-in-out',
    arrows: false,
    beforeChange: (_, newIndex) => {
      setCurrentSlide(newIndex);
      resetProgress();
    },
    responsive: [
      { breakpoint: 1750, settings: { slidesToShow: 3 } },
      { breakpoint: 1023, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 1 } },
    ],
  };

  if (isLoading) return null;
  if (isError || !newsData.length) return null;

  return (
    <div className="py-10 md:py-18 bg-gradient-to-b from-primary-50 to-primary-50/60 bg-koperasi-pattern">
      <ContainerFluid>
        <FadeInStagger className="my-5 lg:my-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-xs sm:text-sm font-medium leading-relaxed tracking-[0.15em] uppercase text-center text-gray-600"
          >
            Berita terbaru
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-semibold leading-snug text-gray-900 mt-2 text-center"
          >
            Baca Informasi Terbaru <span className="text-primary">Koperasi AKR Syariah</span>.
          </motion.h2>

          <div className="relative py-10">
            {/* BUTTON LIHAT SEMUA */}
            <div className="flex justify-end mb-4">
              <Link
                to={`/berita`}
                className="flex p-1 items-center bg-primary-800 text-white rounded-full overflow-hidden transition duration-300 hover:bg-primary-900"
              >
                <span className="bg-white text-primary-800 rounded-full w-10 h-10 flex items-center justify-center">
                  <ArrowUpRight className="h-5 w-5" />
                </span>
                <span className="px-5 py-3 ml-5 text-xs lg:text-sm font-medium rounded-full text-primary bg-primary-100">
                  Lihat Semua
                </span>
              </Link>
            </div>

            <Slider ref={sliderRef} {...settings}>
              {newsData.map((slide) => (
                <NewsCard key={slide.id} slide={slide} />
              ))}
            </Slider>

            {/* DESKTOP CONTROLS */}
            {!isLoading && newsData.length > 1 && (
              <div className="hidden sm:flex relative mt-2 px-4 w-full justify-between items-center">
                <div className="flex gap-4">
                  <PrevArrow onClick={() => sliderRef.current?.slickPrev()} />
                  <NextArrow onClick={() => sliderRef.current?.slickNext()} />
                </div>
                <ProgressDots currentSlide={currentSlide} progress={progress} slides={newsData} />
              </div>
            )}

            {/* MOBILE CONTROLS */}
            {!isLoading && newsData.length > 1 && (
              <div className="flex sm:hidden flex-col items-center gap-3 mt-4 w-full">
                <ProgressDots currentSlide={currentSlide} progress={progress} slides={newsData} />
                <div className="flex gap-4">
                  <PrevArrow onClick={() => sliderRef.current?.slickPrev()} />
                  <NextArrow onClick={() => sliderRef.current?.slickNext()} />
                </div>
              </div>
            )}
          </div>
        </FadeInStagger>
      </ContainerFluid>
    </div>
  );
}

/* ===================== COMPONENT ===================== */
const NewsCard = ({ slide }) => (
  <motion.article
    className="flex flex-col h-full px-1"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex flex-col h-full bg-gradient-to-b from-primary-100 to-primary-500 p-4 rounded-2xl overflow-hidden">
      {/* IMAGE */}
      <div className="relative w-full">
        <img
          src={slide.thumbnail_small}
          alt={slide.title}
          className="aspect-[16/9] w-full rounded-2xl object-cover"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* CONTENT (INILAH KUNCINYA) */}
      <Link to={`/berita/${slide.slug}`} className="flex flex-col flex-1 mt-6">
        {/* META — JANGAN MASUK flex-1 */}
        <div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-center sm:justify-between">
          {/* DATE */}
          <div className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-md text-gray-700 w-fit">
            <Calendar size={12} />
            <span className="whitespace-nowrap">{formatDateIndo(slide.created_at)}</span>
          </div>

          {/* CATEGORY */}
          {slide.category?.name && (
            <div
              className="
      w-fit max-w-full
      rounded-full bg-primary-100
      px-3 py-1
      font-medium text-primary-600
      truncate
      sm:ml-auto
    "
            >
              {slide.category.name}
            </div>
          )}
        </div>

        {/* CONTENT — BOLEH flex-1 */}
        <div className="mt-3 flex flex-col flex-1">
          <H5 className="line-clamp-2 text-slate-200 font-semibold">{slide.title}</H5>

          <BodySmHTML content={slide.content} className="text-slate-300 line-clamp-2 mt-2" />

          {/* spacer */}
          <div className="mt-auto" />
        </div>
      </Link>
    </div>
  </motion.article>
);

// const SkeletonCard = () => (
//   <div className="animate-pulse flex flex-col h-full mx-1 min-h-[360px] bg-gray-200 rounded-2xl p-4" />
// );

const ProgressDots = ({ slides, currentSlide, progress }) => (
  <div className="flex gap-2">
    {slides.map((_, index) => (
      <div
        key={index}
        className={`relative overflow-hidden transition-all duration-500 ${
          currentSlide === index
            ? 'w-40 sm:w-28 h-3 bg-primary-200 rounded-full'
            : 'w-3 h-3 bg-primary-200 rounded-full'
        }`}
      >
        {currentSlide === index && (
          <div
            className="absolute top-0 left-0 h-full bg-primary-500 rounded-full"
            style={{ width: `${progress[index]}%`, transition: 'width 0.1s ease-in-out' }}
          />
        )}
      </div>
    ))}
  </div>
);

const PrevArrow = ({ onClick }) => (
  <button className="text-primary-800 p-2 transition-all duration-300" onClick={onClick}>
    <MoveLeft size={30} />
  </button>
);

const NextArrow = ({ onClick }) => (
  <button className="text-primary-800 p-2 transition-all duration-300" onClick={onClick}>
    <MoveRight size={30} />
  </button>
);

export default NewsHome;
